import React, { useState, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useBreadcrumbs } from "../../features/breadcrumbs/hooks";
import { useGetAppealDetailsQuery } from "../../endpoints/getAppealDetails";
import { useAppSelector } from "../../app/store/hooks";
import LoaderBox from "../loader/LoaderBox";
import { Error404 } from "../../pages/error-404/Error404";
import Page from "../page/Page";
import { Space } from "../space/Space";
import PageHeader from "../../features/page-header/PageHeader";
import { ProgressTrackerComponent } from "./utils/ProgressTracker";
import { StepRenderer } from "./utils/StepRenderer";
import { useNavigationHandlers, useUnloadHandler } from "./utils/utils";

const STEPS = [
    "Review Summary",
    "Select Notices",
    "Select Images",
    "Add attachments",
    "Download Package",
];

export const ExportAppeals: React.FC = () => {
    const { propertyId, year } = useParams();
    const { updateBreadcrumbs } = useBreadcrumbs();
    const [currentStep, setCurrentStep] = useState(0);
    const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
    const { alias } = useAppSelector(state => state.auth.user);
    const current_url = window.location.pathname;

    const { data, isSuccess, isLoading } = useGetAppealDetailsQuery({
        propertyArtifactId: propertyId,
        businessUseCase: "SMART",
        year: year,
    });

    const { doForceUpdate, doNavUpdate } = useNavigationHandlers(
        data,
        alias,
        propertyId,
        current_url
    );

    useUnloadHandler(data, alias, current_url, propertyId, doForceUpdate);

    const updateBreadcrumbsCallback = useCallback(() => {
        if (isSuccess && data) {
            updateBreadcrumbs([
                { text: "Home", href: "/", commandRedirect: () => doNavUpdate() },
                {
                    text: data.appealDetails?.propertyNumber,
                    href: `/property-details/${propertyId}`,
                    commandRedirect: () => doNavUpdate(),
                },
                { text: "Export", href: "#" },
            ]);
        }
    }, [isSuccess, data, propertyId, updateBreadcrumbs, doNavUpdate]);

    useEffect(() => {
        updateBreadcrumbsCallback();
    }, [data]);

    if (isLoading) {
        return <LoaderBox loadingText="Loading property..." />;
    }

    if (!isSuccess) {
        return <Error404 />;
    }

    return (
        <Page
            type="form"
            title={"Export Appeals Package"}
            description={undefined}
            hasBreadcrumbs={true}
            actions={null}
        >
            <Space size={"xs"} />
            <PageHeader />
            <Space size={"l"} />
            <ProgressTrackerComponent currentStep={currentStep} steps={STEPS} />
            <StepRenderer
                currentStep={currentStep}
                handleStepChange={setCurrentStep}
                data={data}
                pdfBlob={pdfBlob}
                setPdfBlob={setPdfBlob}
            />
        </Page>
    );
};
